import type { Lottery, LotteryStatus } from 'api/lotteries';
import { updateOrAppendById } from 'helpers/updateOrAppendById';
import produce from 'immer';

export type State = {
  lotteries: Lottery[];
  lotteriesPending: boolean;
  statuses: LotteryStatus[];
  /** lotteries whose win popup is still to be shown, in order */
  winQueue: number[];
  seenWinIds: number[];
  /** every lottery prize the player has won, ticket or not */
  wonPrizeIds: number[];
  wonPrizesPending: boolean;
};

export enum ACTION_TYPE {
  SET_LOTTERIES = 0,
  UPDATE_LOTTERIES = 1,
  SET_STATUSES = 2,
  UPDATE_STATUS = 3,
  ADD_SEEN_WINS = 4,
  ENQUEUE_WINS = 5,
  MARK_WIN_SEEN = 6,
  RESET_PLAYER = 7,
  DROP_WIN = 8,
  SET_WON_PRIZES = 9,
}

export type Action =
  | { type: ACTION_TYPE.SET_LOTTERIES; payload: { lotteries: Lottery[] } }
  | { type: ACTION_TYPE.UPDATE_LOTTERIES; payload: { lotteries: Lottery[] } }
  | { type: ACTION_TYPE.SET_STATUSES; payload: { statuses: LotteryStatus[] } }
  | {
      type: ACTION_TYPE.UPDATE_STATUS;
      payload: { status: LotteryStatus; isLive?: boolean };
    }
  | { type: ACTION_TYPE.ADD_SEEN_WINS; payload: { lotteryIds: number[] } }
  | { type: ACTION_TYPE.ENQUEUE_WINS; payload: { lotteryIds: number[] } }
  | { type: ACTION_TYPE.MARK_WIN_SEEN; payload: { lotteryId: number } }
  | { type: ACTION_TYPE.RESET_PLAYER }
  | { type: ACTION_TYPE.DROP_WIN; payload: { lotteryId: number } }
  | { type: ACTION_TYPE.SET_WON_PRIZES; payload: { prizeIds: number[] } };

type Reducer = (state: State, action: Action) => State;

/** the channel leaves out the counters REST gives, they stay as they were */
const mergeLottery = (lotteries: Lottery[], incoming: Lottery) => {
  const prev = lotteries.find(({ id }) => id === incoming.id);

  return updateOrAppendById(lotteries, { ...prev, ...incoming }, 'id');
};

const hasNewPrize = (prev: LotteryStatus | undefined, next: LotteryStatus) =>
  Object.keys(next.prizes).some((ticket) => !(prev && ticket in prev.prizes));

const enqueue = (draft: State, lotteryIds: number[]) => {
  for (const id of lotteryIds) {
    if (!(draft.winQueue.includes(id) || draft.seenWinIds.includes(id))) {
      draft.winQueue.push(id);
    }
  }
};

export const reducer: Reducer = (state, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      // merged, a won lottery fetched by id may already be in the list
      case ACTION_TYPE.SET_LOTTERIES:
      case ACTION_TYPE.UPDATE_LOTTERIES:
        for (const lottery of action.payload.lotteries) {
          draft.lotteries = mergeLottery(draft.lotteries, lottery);
        }

        if (action.type === ACTION_TYPE.SET_LOTTERIES) {
          draft.lotteriesPending = false;
        }
        break;
      case ACTION_TYPE.SET_STATUSES:
        for (const status of action.payload.statuses) {
          draft.statuses = updateOrAppendById(
            draft.statuses,
            status,
            'lotteryId'
          );
        }
        break;
      case ACTION_TYPE.UPDATE_STATUS: {
        const { status, isLive } = action.payload;
        const prev = state.statuses.find(
          ({ lotteryId }) => lotteryId === status.lotteryId
        );

        if (isLive && hasNewPrize(prev, status)) {
          enqueue(draft, [status.lotteryId]);
        }

        draft.statuses = updateOrAppendById(
          draft.statuses,
          status,
          'lotteryId'
        );
        break;
      }
      case ACTION_TYPE.ADD_SEEN_WINS:
        draft.seenWinIds = Array.from(
          new Set([...draft.seenWinIds, ...action.payload.lotteryIds])
        );
        draft.winQueue = draft.winQueue.filter(
          (id) => !action.payload.lotteryIds.includes(id)
        );
        break;
      case ACTION_TYPE.ENQUEUE_WINS:
        enqueue(draft, action.payload.lotteryIds);
        break;
      case ACTION_TYPE.MARK_WIN_SEEN:
        draft.winQueue = draft.winQueue.filter(
          (id) => id !== action.payload.lotteryId
        );
        draft.seenWinIds.push(action.payload.lotteryId);
        break;
      // out of the queue without being stored as seen, e.g. nothing to show
      case ACTION_TYPE.DROP_WIN:
        draft.winQueue = draft.winQueue.filter(
          (id) => id !== action.payload.lotteryId
        );
        break;
      case ACTION_TYPE.SET_WON_PRIZES:
        draft.wonPrizeIds = action.payload.prizeIds;
        draft.wonPrizesPending = false;
        break;
      case ACTION_TYPE.RESET_PLAYER:
        draft.statuses = [];
        draft.winQueue = [];
        draft.seenWinIds = [];
        draft.wonPrizeIds = [];
        draft.wonPrizesPending = true;
        break;
    }
  });
